import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { WalkthroughStep } from "./walkthrough-steps";

interface WalkthroughTooltipProps {
  step: WalkthroughStep;
  targetRect: DOMRect | null;
  currentIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

const TOOLTIP_WIDTH = 320;
const GAP = 16;

function computePosition(
  targetRect: DOMRect | null,
  placement: WalkthroughStep["placement"],
  tooltipHeight: number
): { top: number; left: number } {
  // Centered (no target)
  if (!targetRect || placement === "center") {
    return {
      top: Math.max(60, (window.innerHeight - tooltipHeight) / 2),
      left: Math.max(16, (window.innerWidth - TOOLTIP_WIDTH) / 2),
    };
  }

  let top = 0;
  let left = targetRect.x + targetRect.width / 2 - TOOLTIP_WIDTH / 2;

  switch (placement) {
    case "bottom":
      top = targetRect.bottom + GAP;
      break;
    case "top":
      top = targetRect.top - tooltipHeight - GAP;
      break;
    case "left":
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      left = targetRect.left - TOOLTIP_WIDTH - GAP;
      break;
    case "right":
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      left = targetRect.right + GAP;
      break;
  }

  // Clamp to viewport (min top 72px to clear sticky nav)
  left = Math.max(16, Math.min(left, window.innerWidth - TOOLTIP_WIDTH - 16));
  top = Math.max(72, Math.min(top, window.innerHeight - tooltipHeight - 16));

  return { top, left };
}

export function WalkthroughTooltip({
  step,
  targetRect,
  currentIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
}: WalkthroughTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipHeight = tooltipRef.current?.offsetHeight ?? 200;
  const pos = computePosition(targetRect, step.placement, tooltipHeight);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSkip();
      else if (e.key === "ArrowRight" || e.key === "Enter") onNext();
      else if (e.key === "ArrowLeft" && currentIndex > 0) onPrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onSkip, onNext, onPrev, currentIndex]);

  return (
    <motion.div
      ref={tooltipRef}
      className="fixed z-[61] bg-white dark:bg-gray-900 rounded-xl shadow-2xl border p-5"
      style={{ width: TOOLTIP_WIDTH }}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0, top: pos.top, left: pos.left }}
      transition={{
        opacity: { duration: 0.2 },
        y: { duration: 0.2 },
        top: { type: "spring", stiffness: 300, damping: 30 },
        left: { type: "spring", stiffness: 300, damping: 30 },
      }}
    >
      <h3 className="text-base font-semibold mb-1">{step.title}</h3>
      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
        {step.description}
      </p>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {currentIndex + 1} / {totalSteps}
        </span>
        <div className="flex gap-2">
          {currentIndex < totalSteps - 1 && (
            <Button variant="ghost" size="sm" onClick={onSkip}>
              Skip
            </Button>
          )}
          {currentIndex > 0 && (
            <Button variant="outline" size="sm" onClick={onPrev}>
              Back
            </Button>
          )}
          <Button size="sm" onClick={onNext}>
            {currentIndex === totalSteps - 1 ? "Let\u2019s Go!" : "Next"}
          </Button>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-1.5 mt-3">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div
            key={i}
            className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              i === currentIndex
                ? "w-6 bg-primary"
                : i < currentIndex
                  ? "w-1.5 bg-primary/40"
                  : "w-1.5 bg-muted"
            )}
          />
        ))}
      </div>
    </motion.div>
  );
}
